import { useSetState } from "minimal-shared/hooks";
import { useMemo, useEffect, useCallback } from "react";

import axios, { endpoints } from "src/lib/axios";

import { JWT_STORAGE_KEY } from "./constant";
import { AuthContext } from "../auth-context";
import { setSession, isValidToken } from "./utils";

import type { AuthState } from "../../types";

// ----------------------------------------------------------------------

/**
 * NOTE:
 * We only build demo at basic level.
 * Customer will need to do some extra handling yourself if you want to extend the logic and other features...
 */

type Props = {
  children: React.ReactNode;
};

export function AuthProvider({ children }: Props) {
  const { state, setState } = useSetState<AuthState>({
    user: null,
    loading: true,
  });

  const checkUserSession = useCallback(async () => {
    try {
      const accessToken = sessionStorage.getItem(JWT_STORAGE_KEY);

      if (accessToken && isValidToken(accessToken)) {
        setSession(accessToken);

        const res = await axios.get(endpoints.auth.me);

        const { user } = res.data;

        setState({ user: { ...user, accessToken }, loading: false });
      } else {
        setState({ user: null, loading: false });
      }
    } catch (error) {
      console.error(error);
      setState({ user: null, loading: false });
    }
  }, [setState]);
  // const { state, setState } = useSetState<AuthState>({
  //   user: { id: "123", name: "Demo User", role: "admin" }, // Dummy user to skip authentication
  //   loading: false,
  // });

  // const checkUserSession = useCallback(async () => {
  //   setState({
  //     user: { id: "123", name: "Demo User", role: "admin" },
  //     loading: false,
  //   }); // Dummy user
  // }, [setState]);

  useEffect(() => {
    checkUserSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ----------------------------------------------------------------------

  const checkAuthenticated = state.user ? "authenticated" : "unauthenticated";

  const status = state.loading ? "loading" : checkAuthenticated;

  // const memoizedValue = useMemo(
  //   () => ({
  //     user: state.user,
  //     checkUserSession,
  //     loading: false,
  //     authenticated: true, // Always authenticated
  //     unauthenticated: false,
  //   }),
  //   [checkUserSession, state.user]
  // );
  const memoizedValue = useMemo(
    () => ({
      user: state.user
        ? { ...state.user, role: state.user?.role ?? "admin" }
        : null,
      checkUserSession,
      loading: status === "loading",
      authenticated: status === "authenticated",
      unauthenticated: status === "unauthenticated",
    }),
    [checkUserSession, state.user, status]
  );

  return (
    <AuthContext.Provider value={memoizedValue}>
      {children}
    </AuthContext.Provider>
  );
}
